import React, { useEffect, useCallback, useMemo } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  getAdminProfile,
  getAdmins,
  getStudentApplication,
  retrieveStudentDetails,
} from "../../services/admin.services";
import { useDispatch, useSelector } from "react-redux";
import { setAdmins, setSelectedAdmin } from "../../state/reducers/adminSlice";
import { setAllCustomers } from "../../state/reducers/customerSlice";
import { getCustomers } from "../../services/customer.services";
import { setLeads } from "../../state/reducers/leadSlice";
import axios from "axios";
import { getLeads } from "../../services/lead.services";
import { setInvoices } from "../../state/reducers/invoiceSlice";
import { getInvoices } from "../../services/invoice.services";
import { setDirectories } from "../../state/reducers/resourcesSlice";
import { clearUser, setUserDetails } from "../../state/reducers/userSlice";
import {
  getAbroadCounselingServices,
  getCareerCounselingServices,
  getOtherServices,
  getTestPreparationServices,
} from "../../services/user.services";
import { setServices } from "../../state/reducers/servicesSlice";
import {
  toggleReminderModal,
  toggleToDoTaskModal,
} from "../../state/reducers/applicationSlice";
import {
  addNewTask,
  fetchTasks,
  resetSelectedReminder,
  resetSelectedTask,
} from "../../state/reducers/dashboardSlice";
import AddTaskModal from "../../components/admin/dashboard/AddTaskModal";
import AddReminderModal from "../../components/admin/dashboard/AddReminderModal";
import { getsuperAdminProfile } from "../../services/superadmin.services";
import { setCareerFields } from "../../state/reducers/manageDetailsSlice";
import { getDomain } from "../../services/postService.services";

const Main = React.memo(() => {
  const isToDoTaskModalOpen = useSelector(
    (state) => state.application.isToDoTaskModalOpen
  );
  const isReminderModalOpen = useSelector(
    (state) => state.application.isReminderModalOpen
  );
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();
  const user_id = useSelector((state) => state.user.user_id);
  const role = useSelector((state) => state.user.role);
  const navigate = useNavigate();

  // Memoize this function as it doesn't depend on any props or state
  const isTokenExpired = useCallback(() => {
    const expirationTime = localStorage.getItem("expires");
    if (!expirationTime) return true; // No expiration time set, treat as expired
    const currentTime = new Date().getTime();
    return currentTime > expirationTime;
  }, []);

  // Memoize handler functions with useCallback
  const handleTokenExpiration = useCallback(() => {
    if (isTokenExpired()) {
      // Redirecting to login page
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, [isTokenExpired]);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = response.data?.results.Leads || [];
      console.log("response", response);
      dispatch(setLeads(data.length > 0 && data)); // Use default leads if API fails
    } catch (err) {
      console.error("Error fetching leads, using fallback:", err);
    }
  }, [dispatch, isTokenExpired]);

  const getAllLeads = useCallback(async () => {
    try {
      if (isTokenExpired()) {
        console.log("Token expired. Redirecting to login.");
        return; // Stop API call if token expired
      }
      const response = await getLeads();
      const data = response.data?.Leads || [];

      if (role === "superadmin") {
        dispatch(setLeads(data.length > 0 && data));
      } else {
        const filteredLeads = data.filter(
          (lead) => lead.created_by === user_id
        );
        dispatch(setLeads(filteredLeads.length > 0 && filteredLeads));
      }
    } catch (err) {
      console.error(err);
    }
  }, [dispatch, isTokenExpired, role, user_id]);

  const fetchDomains = useCallback(async () => {
    try {
      const res = await getDomain();
      dispatch(setCareerFields(res.data));
    } catch (error) {
      console.error("Error fetching domains:", error);
    }
  }, [dispatch]);

  const getAllAdmins = useCallback(async () => {
    try {
      if (isTokenExpired()) {
        console.log("Token expired. Redirecting to login.");
        return;
      }
      const res = await getAdmins();
      dispatch(setAdmins(res.data));
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, isTokenExpired]);

  const getAllCustomers = useCallback(async () => {
    try {
      if (isTokenExpired()) {
        console.log("Token expired. Redirecting to login.");
        return;
      }
      const res = await getCustomers();
      dispatch(setAllCustomers(res.data.data || []));
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, isTokenExpired]);

  const addTask = useCallback(
    async (task) => {
      await dispatch(addNewTask(task));
      await dispatch(fetchTasks());
    },
    [dispatch]
  );

  const handleClose = useCallback(() => {
    dispatch(toggleToDoTaskModal(false));
    dispatch(resetSelectedTask());
  }, [dispatch]);

  const handleReminderClose = useCallback(() => {
    dispatch(toggleReminderModal(false));
    dispatch(resetSelectedReminder());
  }, [dispatch]);

  const getProfile = useCallback(async () => {
    try {
      if (isTokenExpired()) {
        console.log("Token expired. Redirecting to login.");
        return;
      }
      if (role === "admin") {
        const res = await getAdminProfile(user_id);
        dispatch(setUserDetails(res.data));
        dispatch(setSelectedAdmin(res.data.admin_id));
      } else if (role === "superadmin") {
        const res = await getsuperAdminProfile(user_id);
        const data = {
          ...res.data,
          user_id: res.data.superadmin_id,
        };
        dispatch(setUserDetails(data));
      } else {
        const res = await retrieveStudentDetails(user_id);
        dispatch(setUserDetails(res.data));
      }
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, isTokenExpired, role, user_id]);

  const getAllInvoices = useCallback(async () => {
    try {
      if (isTokenExpired()) {
        console.log("Token expired. Redirecting to login.");
        return;
      }
      const res = await getInvoices();
      dispatch(setInvoices(res.data));
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, isTokenExpired]);

  // Group all service loading functions into one callback
  const loadAllServices = useCallback(async () => {
    if (isTokenExpired()) {
      console.log("Token expired. Redirecting to login.");
      return;
    }

    try {
      const [abroadData, testPrepData, careerData, otherData] =
        await Promise.all([
          getAbroadCounselingServices(),
          getTestPreparationServices(),
          getCareerCounselingServices(),
          getOtherServices(),
        ]);

      dispatch(
        setServices({
          abroad_counselling_list: abroadData,
          test_prep_list: testPrepData,
          career_counselling_list: careerData,
          other_services_list: otherData,
        })
      );
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, [dispatch, isTokenExpired]);

  // Memoize the loadInitialData function to avoid recreation on each render
  const loadInitialData = useCallback(() => {
    handleTokenExpiration();
    getAllCustomers();
    getAllAdmins();
    getAllInvoices();
    getProfile();
    loadAllServices();
    fetchLeads();
    fetchDomains();
  }, [
    handleTokenExpiration,
    getAllCustomers,
    getAllAdmins,
    getAllInvoices,
    getProfile,
    loadAllServices,
    fetchLeads,
    fetchDomains,
  ]);

  // Effect for token validation
  useEffect(() => {
    if (!token) {
      navigate("/login");
      dispatch(clearUser());
    }
  }, [token, navigate, dispatch]);

  // Effect for initial data loading
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Effect for leads (depends on user_id)
  useEffect(() => {
    getAllLeads();
  }, [getAllLeads]);

  return (
    <div>
      {isToDoTaskModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm z-[1000]">
          <AddTaskModal
            isOpen={isToDoTaskModalOpen}
            onClose={handleClose}
            onAddTask={addTask}
          />
        </div>
      )}
      {isReminderModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm z-[1000]">
          <AddReminderModal
            isOpen={isReminderModalOpen}
            onReminderClose={handleReminderClose}
            onAddTask={addTask}
          />
        </div>
      )}
      <Outlet />
    </div>
  );
});

export default Main;
